import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import noop from 'lodash/noop';

import {
  CreateVariantRequest,
  Manufacturer,
  Product,
  ProductVariant,
  Series,
  UpdateVariantRequest,
} from '../../types/catalogue';

// ─── State ───────────────────────────────────────────────────────────────────

/**
 * Tracks which entity IDs have had their children fetched from the API,
 * so we can skip redundant network calls on re-selection.
 */
interface FetchedIds {
  /** Manufacturer IDs whose series list has been loaded. */
  manufacturerSeries: Record<string, true>;
  /** Series IDs whose products list has been loaded. */
  seriesProducts: Record<string, true>;
  /** Product IDs whose detail (variants) has been loaded. */
  productDetails: Record<string, true>;
}

export interface CatalogueState {
  manufacturers: Manufacturer[];
  selectedManufacturerId: string | null;
  selectedSeriesId: string | null;
  selectedProductId: string | null;
  selectedVariantId: string | null;
  isLoading: boolean;
  isLoadingProductDetail: boolean;
  error: string | null;
  fetchedIds: FetchedIds;
}

const initialState: CatalogueState = {
  manufacturers: [],
  selectedManufacturerId: null,
  selectedSeriesId: null,
  selectedProductId: null,
  selectedVariantId: null,
  isLoading: false,
  isLoadingProductDetail: false,
  error: null,
  fetchedIds: {
    manufacturerSeries: {},
    seriesProducts: {},
    productDetails: {},
  },
};

// ─── Payload Interfaces ──────────────────────────────────────────────────────

interface ModifyManufacturerPayload {
  id: string;
  data: { name?: string; description?: string; logoBase64?: string };
}

interface CreateSeriesPayload {
  manufacturerId: string;
  name: string;
  description?: string;
}

interface ModifySeriesPayload {
  id: string;
  data: { name?: string; description?: string };
}

interface CreateProductPayload {
  seriesId: string;
  name: string;
  description?: string;
  imageBase64?: string;
}

interface ModifyProductPayload {
  id: string;
  data: { name?: string; description?: string; status?: string; imageBase64?: string };
}

interface CreateVariantPayload {
  productId: string;
  data: CreateVariantRequest;
  ldcFile?: File;
}

interface ModifyVariantPayload {
  id: string;
  data: UpdateVariantRequest;
  ldcFile?: File;
}

// ─── Slice ───────────────────────────────────────────────────────────────────

export const catalogueReducer = createSlice({
  name: 'catalogue',
  initialState,
  reducers: {
    // ── Saga triggers (picked up by catalogue.saga.ts) ────────────────────
    fetchManufacturers: (_state: CatalogueState) => noop(),
    fetchSeriesByManufacturer: (_state: CatalogueState, _action: PayloadAction<string>) => noop(),
    fetchProductsBySeries: (_state: CatalogueState, _action: PayloadAction<string>) => noop(),
    fetchProductDetail: (_state: CatalogueState, _action: PayloadAction<string>) => noop(),

    createManufacturer: (_state: CatalogueState, _action: PayloadAction<{ name: string; logoBase64?: string }>) =>
      noop(),
    modifyManufacturer: (_state: CatalogueState, _action: PayloadAction<ModifyManufacturerPayload>) => noop(),
    removeManufacturer: (_state: CatalogueState, _action: PayloadAction<string>) => noop(),

    createSeries: (_state: CatalogueState, _action: PayloadAction<CreateSeriesPayload>) => noop(),
    modifySeries: (_state: CatalogueState, _action: PayloadAction<ModifySeriesPayload>) => noop(),
    removeSeries: (_state: CatalogueState, _action: PayloadAction<string>) => noop(),

    createProduct: (_state: CatalogueState, _action: PayloadAction<CreateProductPayload>) => noop(),
    modifyProduct: (_state: CatalogueState, _action: PayloadAction<ModifyProductPayload>) => noop(),
    removeProduct: (_state: CatalogueState, _action: PayloadAction<string>) => noop(),

    createVariant: (_state: CatalogueState, _action: PayloadAction<CreateVariantPayload>) => noop(),
    modifyVariant: (_state: CatalogueState, _action: PayloadAction<ModifyVariantPayload>) => noop(),
    removeVariant: (_state: CatalogueState, _action: PayloadAction<string>) => noop(),

    // Sync (admin-only) — triggers a full catalogue sync on the backend.
    syncCatalogue: (_state: CatalogueState) => noop(),

    // ── Selection (state mutations) ───────────────────────────────────────
    selectManufacturer: (state: CatalogueState, action: PayloadAction<string | null>) => {
      state.selectedManufacturerId = action.payload;
      state.selectedSeriesId = null;
      state.selectedProductId = null;
      state.selectedVariantId = null;
    },

    selectSeries: (state: CatalogueState, action: PayloadAction<string | null>) => {
      state.selectedSeriesId = action.payload;
      state.selectedProductId = null;
      state.selectedVariantId = null;
    },

    selectProduct: (state: CatalogueState, action: PayloadAction<string | null>) => {
      state.selectedProductId = action.payload;
      if (!action.payload) {
        state.selectedVariantId = null;
      } else {
        const mfr = state.manufacturers.find((b) => b.id === state.selectedManufacturerId);
        const series = mfr?.series.find((s) => s.id === state.selectedSeriesId);
        const product = series?.products.find((p) => p.id === action.payload);
        state.selectedVariantId = product?.variants.length ? product.variants[0].id : null;
      }
    },

    selectVariant: (state: CatalogueState, action: PayloadAction<string | null>) => {
      state.selectedVariantId = action.payload;
    },

    resetSelection: (state: CatalogueState) => {
      state.selectedManufacturerId = null;
      state.selectedSeriesId = null;
      state.selectedProductId = null;
      state.selectedVariantId = null;
    },

    // ── Loading / error ───────────────────────────────────────────────────
    setLoading: (state: CatalogueState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setLoadingProductDetail: (state: CatalogueState, action: PayloadAction<boolean>) => {
      state.isLoadingProductDetail = action.payload;
    },

    setError: (state: CatalogueState, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // ── Manufacturer state mutations ───────────────────────────────────────
    setManufacturers: (state: CatalogueState, action: PayloadAction<Manufacturer[]>) => {
      state.manufacturers = action.payload;
      // Full list replaced with empty children — clear all caches.
      state.fetchedIds = { manufacturerSeries: {}, seriesProducts: {}, productDetails: {} };
    },

    addManufacturerToList: (state: CatalogueState, action: PayloadAction<Manufacturer>) => {
      state.manufacturers.push(action.payload);
    },

    updateManufacturerInList: (
      state: CatalogueState,
      action: PayloadAction<{ id: string; changes: Partial<Omit<Manufacturer, 'series'>> }>,
    ) => {
      const mfr = state.manufacturers.find((b) => b.id === action.payload.id);
      if (mfr) Object.assign(mfr, action.payload.changes);
    },

    removeManufacturerFromList: (state: CatalogueState, action: PayloadAction<string>) => {
      state.manufacturers = state.manufacturers.filter((b) => b.id !== action.payload);
      if (state.selectedManufacturerId === action.payload) {
        state.selectedManufacturerId = null;
        state.selectedSeriesId = null;
        state.selectedProductId = null;
        state.selectedVariantId = null;
      }
    },

    // ── Series state mutations ────────────────────────────────────────────
    setSeriesForManufacturer: (
      state: CatalogueState,
      action: PayloadAction<{
        manufacturerId: string;
        series: Series[];
        manufacturerUpdates?: Partial<Omit<Manufacturer, 'series'>>;
      }>,
    ) => {
      const mfr = state.manufacturers.find((b) => b.id === action.payload.manufacturerId);
      if (mfr) {
        mfr.series = action.payload.series;
        if (action.payload.manufacturerUpdates) Object.assign(mfr, action.payload.manufacturerUpdates);
      }
      state.fetchedIds.manufacturerSeries[action.payload.manufacturerId] = true;
    },

    addSeriesToManufacturer: (
      state: CatalogueState,
      action: PayloadAction<{ manufacturerId: string; series: Series }>,
    ) => {
      const mfr = state.manufacturers.find((b) => b.id === action.payload.manufacturerId);
      if (mfr) mfr.series.push(action.payload.series);
      // Invalidate so a re-select will re-fetch from the API.
      delete state.fetchedIds.manufacturerSeries[action.payload.manufacturerId];
    },

    updateSeriesInList: (
      state: CatalogueState,
      action: PayloadAction<{ id: string; changes: Partial<Omit<Series, 'products'>> }>,
    ) => {
      for (const mfr of state.manufacturers) {
        const series = mfr.series.find((s) => s.id === action.payload.id);
        if (series) {
          Object.assign(series, action.payload.changes);
          break;
        }
      }
    },

    removeSeriesFromList: (state: CatalogueState, action: PayloadAction<string>) => {
      for (const mfr of state.manufacturers) {
        const before = mfr.series.length;
        mfr.series = mfr.series.filter((s) => s.id !== action.payload);
        if (mfr.series.length < before) {
          delete state.fetchedIds.manufacturerSeries[mfr.id];
        }
      }
      delete state.fetchedIds.seriesProducts[action.payload];
      if (state.selectedSeriesId === action.payload) {
        state.selectedSeriesId = null;
        state.selectedProductId = null;
        state.selectedVariantId = null;
      }
    },

    // ── Product state mutations ───────────────────────────────────────────
    setProductsForSeries: (
      state: CatalogueState,
      action: PayloadAction<{
        seriesId: string;
        products: Product[];
        seriesUpdates?: Partial<Omit<Series, 'products'>>;
      }>,
    ) => {
      for (const mfr of state.manufacturers) {
        const series = mfr.series.find((s) => s.id === action.payload.seriesId);
        if (series) {
          series.products = action.payload.products;
          if (action.payload.seriesUpdates) Object.assign(series, action.payload.seriesUpdates);
          break;
        }
      }
      state.fetchedIds.seriesProducts[action.payload.seriesId] = true;
    },

    addProductToSeries: (state: CatalogueState, action: PayloadAction<{ seriesId: string; product: Product }>) => {
      for (const mfr of state.manufacturers) {
        const series = mfr.series.find((s) => s.id === action.payload.seriesId);
        if (series) {
          series.products.push(action.payload.product);
          break;
        }
      }
      delete state.fetchedIds.seriesProducts[action.payload.seriesId];
    },

    updateProductInList: (
      state: CatalogueState,
      action: PayloadAction<{ id: string; changes: Partial<Omit<Product, 'variants'>> }>,
    ) => {
      for (const mfr of state.manufacturers) {
        for (const series of mfr.series) {
          const product = series.products.find((p) => p.id === action.payload.id);
          if (product) {
            Object.assign(product, action.payload.changes);
            return;
          }
        }
      }
    },

    removeProductFromList: (state: CatalogueState, action: PayloadAction<string>) => {
      for (const mfr of state.manufacturers) {
        for (const series of mfr.series) {
          const before = series.products.length;
          series.products = series.products.filter((p) => p.id !== action.payload);
          if (series.products.length < before) {
            delete state.fetchedIds.seriesProducts[series.id];
          }
        }
      }
      delete state.fetchedIds.productDetails[action.payload];
      if (state.selectedProductId === action.payload) {
        state.selectedProductId = null;
        state.selectedVariantId = null;
      }
    },

    // ── Product detail (variants) state mutations ─────────────────────────
    setProductDetail: (
      state: CatalogueState,
      action: PayloadAction<{ productId: string; changes: Partial<Product> }>,
    ) => {
      for (const mfr of state.manufacturers) {
        for (const series of mfr.series) {
          const product = series.products.find((p) => p.id === action.payload.productId);
          if (product) {
            Object.assign(product, action.payload.changes);
            break;
          }
        }
      }
      state.fetchedIds.productDetails[action.payload.productId] = true;
    },

    addVariantToProduct: (
      state: CatalogueState,
      action: PayloadAction<{ productId: string; variant: ProductVariant }>,
    ) => {
      for (const mfr of state.manufacturers) {
        for (const series of mfr.series) {
          const product = series.products.find((p) => p.id === action.payload.productId);
          if (product) {
            product.variants.push(action.payload.variant);
            delete state.fetchedIds.productDetails[action.payload.productId];
            return;
          }
        }
      }
    },

    updateVariantInList: (
      state: CatalogueState,
      action: PayloadAction<{ id: string; changes: Partial<ProductVariant> }>,
    ) => {
      for (const mfr of state.manufacturers) {
        for (const series of mfr.series) {
          for (const product of series.products) {
            const variant = product.variants.find((v) => v.id === action.payload.id);
            if (variant) {
              Object.assign(variant, action.payload.changes);
              return;
            }
          }
        }
      }
    },

    removeVariantFromList: (state: CatalogueState, action: PayloadAction<string>) => {
      for (const mfr of state.manufacturers) {
        for (const series of mfr.series) {
          for (const product of series.products) {
            const before = product.variants.length;
            product.variants = product.variants.filter((v) => v.id !== action.payload);
            if (product.variants.length < before) {
              delete state.fetchedIds.productDetails[product.id];
            }
          }
        }
      }
      if (state.selectedVariantId === action.payload) {
        state.selectedVariantId = null;
      }
    },
  },
});
